import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Typography } from '@mui/material';
import api from '../../services/api';

const statusLabel = status => ({ CREATED: 'Creato', SAVED: 'Salvato', APPROVED: 'Approvato', VOIDED: 'Annullato',
  COMPLETED: 'Completato', PAYER_ACTION_REQUIRED: 'In attesa del cliente', PENDING: 'In attesa',
  DECLINED: 'Rifiutato', DENIED: 'Rifiutato', PARTIALLY_REFUNDED: 'Rimborso parziale', REFUNDED: 'Rimborsato',
  FAILED: 'Non riuscito', UNKNOWN: 'Da verificare' }[status] || 'Da verificare');

export default function PayPalProviderStatus({ invoiceId, attemptId, canClose = false, amount }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [historyChecked, setHistoryChecked] = useState(false);
  const queryClient = useQueryClient();
  const inspection = useMutation({
    mutationFn: async () => (await api.get(`/paypal/invoices/${invoiceId}/attempts/${attemptId}/provider-status`)).data.data,
    retry: false,
  });
  const { mutate, data, error, isPending } = inspection;
  const closure = useMutation({
    mutationFn: () => api.post(`/paypal/invoices/${invoiceId}/attempts/${attemptId}/close`, { business_history_checked: true }),
    retry: false,
    onSuccess: async () => {
      setConfirmOpen(false); setHistoryChecked(false); inspection.reset();
      await queryClient.invalidateQueries({ queryKey: ['paypal-invoice-attempts', invoiceId] });
    },
  });
  const eligible = canClose && data && !isPending && !error &&
    (!data.available || (data.has_capture === false && ['CREATED', 'SAVED', 'PAYER_ACTION_REQUIRED', 'APPROVED', 'VOIDED'].includes(data.provider_status)));
  const closeDialog = () => { if (!closure.isPending) { setConfirmOpen(false); setHistoryChecked(false); } };
  return <Box sx={{ my: 1 }}>
    <Button size="small" onClick={() => mutate()} disabled={isPending || closure.isPending}>
      {isPending ? 'Verifica in corso…' : 'Verifica stato su PayPal'}
    </Button>
    {error && <Alert severity="error">{error.response?.data?.message || 'Stato PayPal non disponibile. Nessuna operazione di pagamento eseguita.'}</Alert>}
    {data && !isPending && !error && <Alert severity={data.available ? 'info' : 'warning'}>
      <Typography variant="body2" fontWeight="bold">Verifica in sola lettura · {data.environment === 'live' ? 'Live' : 'Sandbox'}</Typography>
      <Typography variant="body2">Controllato: {new Date(data.checked_at).toLocaleString('it-IT', { timeZone: 'Europe/Rome' })}</Typography>
      {!data.available ? <Typography variant="body2">
        Ordine non disponibile nelle API PayPal. Questo esito non dimostra l’assenza di un incasso: confronta la cronologia del conto Business. Il tentativo resta da verificare.
      </Typography> : <>
        <Typography variant="body2">Stato dell’ordine su PayPal: {statusLabel(data.provider_status)} ({data.provider_status})</Typography>
        {data.has_capture ? <>
          <Typography variant="body2">Acquisizione su PayPal: {statusLabel(data.capture.status)} ({data.capture.status})</Typography>
          <Typography variant="body2">Codice transazione su PayPal: {data.capture.id}</Typography>
        </> : <Typography variant="body2">PayPal non riporta un’acquisizione per questo ordine al momento della verifica.</Typography>}
      </>}
      <Typography variant="body2">Questa verifica non acquisisce denaro e non modifica il tentativo o la contabilità.</Typography>
    </Alert>}
    {closure.isSuccess && <Alert severity="success">Tentativo interrotto nel gestionale. Storico conservato; nessun movimento contabile creato.</Alert>}
    {eligible && <Button size="small" color="warning" sx={{ mt: 1 }} onClick={() => { closure.reset(); setConfirmOpen(true); }}>
      Interrompi questo tentativo
    </Button>}
    <Dialog open={confirmOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
      <DialogTitle>Interrompi il vecchio tentativo</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>Il gestionale bloccherà l’acquisizione di questo ordine e conserverà lo storico. La fattura potrà avere un nuovo tentativo di pagamento.</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>Questa operazione non annulla ordini e non esegue addebiti o rimborsi su PayPal. Le notifiche verificate di eventuali incassi già effettuati continueranno a essere elaborate.</Alert>
        <Typography variant="body2" sx={{ mb: 1, overflowWrap: 'anywhere' }}>Ordine PayPal: {data?.provider_order_id}</Typography>
        <FormControlLabel control={<Checkbox checked={historyChecked} disabled={closure.isPending} onChange={event => setHistoryChecked(event.target.checked)} />}
          label={`Ho controllato la cronologia del conto Business ${data?.environment === 'live' ? 'reale' : 'Sandbox'} dell’azienda: non risulta l’incasso di ${new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(amount || 0))} relativo a questo tentativo.`} />
        {closure.error && <Alert severity="error">{closure.error.response?.data?.message || 'Interruzione non riuscita. Il tentativo resta da verificare.'}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} disabled={closure.isPending}>Indietro</Button>
        <Button color="warning" variant="contained" disabled={!historyChecked || closure.isPending} onClick={() => closure.mutate()}>
          {closure.isPending ? 'Interruzione…' : 'Conferma interruzione'}
        </Button>
      </DialogActions>
    </Dialog>
  </Box>;
}
